import * as ElasticAppSearch from '@elastic/app-search-javascript';
import { isAttribute } from '../helpers/is-attribute';
import { getIndexInFilters } from '../helpers/filters';

export class ClientManager {
	constructor() {
		this.client = null;
	}

	/**
	 * Perform a search. Wraps around the Elastic App Search client's search since we need to do a custom disjunctiveSearch disjunctiveSearch is set.
	 *
	 * @param {string} query   Query string
	 * @param {Object} options Options for the request.
	 * @return {Promise} Promise that resolves to the results.
	 */
	search( query, options ) {
		// We need the value but don't want to actually send it to Elasticsearch
		const showVariationsOnlyForAttributes = options.showVariationsOnlyForAttributes;
		delete options.showVariationsOnlyForAttributes;

		// Check if disjunctive search is disabled. If so just remove the disjunctiveFacets key from options and do a normal search.
		if ( window.QalaElasticCore?.isDisjunctiveSearchDisabled && options && options.disjunctiveFacets ) {
			delete options.disjunctiveFacets;
			return this.getClient().search( query, options );
		}

		/**
		 * qalaElasticCore.filter.isCustomDisjunctiveSearch
		 *
		 * Check if we need to do a custom disjunctive search.
		 *
		 * @param {boolean} isCustomDisjunctiveSearch
		 * @return {boolean}
		 */
		const isCustomDisjunctiveSearch = wp.hooks.applyFilters( 'qalaElasticCore.filter.isCustomDisjunctiveSearch', false );

		// No need to do anything custom if there's no disjunctive facets.
		if ( ! isCustomDisjunctiveSearch || ! options || ! options.disjunctiveFacets ) {
			return this.getClient().search( query, options );
		}

		// Figure out which facets are in this query / are an attribute. For these we need a disjunctive search.
		const usedDisjunctiveFacets = options.disjunctiveFacets.filter( facetKey => {
			return (
				( isAttribute( facetKey ) && ( getIndexInFilters( options, facetKey, 'all' ) > -1 || getIndexInFilters( options, facetKey, 'any' ) > -1 ) ) ||
				( ! isAttribute( facetKey ) && ( getIndexInFilters( options, facetKey, 'all' ) > -1 || getIndexInFilters( options, facetKey, 'any' ) > -1 ) )
			);
		} ).map( facetKey => {
			return {
				facetKey,
				filterIndexAll: getIndexInFilters( options, facetKey, 'all' ),
				filterIndexAny: getIndexInFilters( options, facetKey, 'any' ),
			};
		} );

		const allDisjunctiveFacets = options.disjunctiveFacets.map( facetKey => {
			return {
				facetKey,
				filterIndexAll: getIndexInFilters( options, facetKey, 'all' ),
				filterIndexAny: getIndexInFilters( options, facetKey, 'any' ),
			};
		} );

		// Get all required filters if any.
		const requiredFilters = window.QalaElasticCore?.requiredFilters || [];

		// Remove disjunctiveFacets key from options (since we're implementing it manually)
		delete options.disjunctiveFacets;

		// Go through all filters under disjunctiveFacets, and create a new query for each of them.
		const disjunctiveSearchesPromises = usedDisjunctiveFacets.map( ( { facetKey, filterIndexAll, filterIndexAny } ) => {
			const newOptions = JSON.parse( JSON.stringify( options ) ); // just spreading doesn't work since it creates a shallow copy.

			// If the facetKey is found in the newOptions.filters.all array, remove it.
			if ( filterIndexAll !== -1 ) {
				newOptions.filters.all = [ ...newOptions.filters.all.filter( filter => Object.keys( filter )[ 0 ] !== facetKey ) ];
			}

			// If the facetKey is found in the newOptions.filters.any array, remove it.
			if ( filterIndexAny !== -1 ) {
				newOptions.filters.any = [ ...newOptions.filters.any.filter( filter => Object.keys( filter )[ 0 ] !== facetKey ) ];
			}

			// Check if facetKey is an attribute. If so we need to either remove (if it's there) the `object_type` filter from none.
			if ( isAttribute( facetKey ) ) {
				const filterIndexNone = getIndexInFilters( newOptions, 'object_type', 'none' );

				// If the object_type filter is found in the options.filters.none array, remove it from there.
				if ( filterIndexNone !== -1 ) {
					newOptions.filters.none.splice( filterIndexNone, 1 );
				}
			}

			// Remove all other facets other than the one we're doing a disjunctive search for.
			newOptions.facets = { [ facetKey ]: newOptions.facets[ facetKey ] };

			// Append no analytics to the query so that the search doesn't get logged.
			newOptions.analytics = { tags: [ 'Facet-Only' ] };

			// Also set page size to 0 so that we don't get any results.
			newOptions.page.size = 0;

			// Append all required filters as well.
			requiredFilters.forEach( ( { key, value } ) => newOptions.filters.all.push( { [ key ]: value } ) );

			return this.getClient().search( query, newOptions );
		} );

		// If this is enabled, we need to do another disjunctive search for all attributes to get their correct numbers. If we don7t need to this is just
		// an empty promise.
		let attributesDisjunctiveSearch = new Promise( resolve => resolve() );
		if ( showVariationsOnlyForAttributes ) {
			const newOptions = JSON.parse( JSON.stringify( options ) ); // just spreading doesn't work since it creates a shallow copy.
			allDisjunctiveFacets.filter( ( { facetKey } ) => isAttribute( facetKey ) ).forEach( ( { facetKey, filterIndexAll, filterIndexAny } ) => {
				// If the facetKey is found in the newOptions.filters.all array, remove it.
				if ( filterIndexAll !== -1 ) {
					newOptions.filters.all = [ ...newOptions.filters.all.filter( filter => Object.keys( filter )[ 0 ] !== facetKey ) ];
				}

				// If the facetKey is found in the newOptions.filters.any array, remove it.
				if ( filterIndexAny !== -1 ) {
					newOptions.filters.any = [ ...newOptions.filters.any.filter( filter => Object.keys( filter )[ 0 ] !== facetKey ) ];
				}
			} );

			// Check if facetKey is an attribute. If so we need to either remove (if it's there) the `object_type` filter from none.
			// Otherwise we remove this facet completely
			const filterIndexNone = getIndexInFilters( newOptions, 'object_type', 'none' );

			// If the object_type filter is found in the options.filters.none array, remove it from there.
			if ( filterIndexNone !== -1 ) {
				newOptions.filters.none.splice( filterIndexNone, 1 );
			}

			// Remove all facets that are not disjunctive from facets (but keep them in query)
			Object.keys( newOptions.facets ).forEach( key => {
				if ( ! isAttribute( key ) ) {
					delete newOptions.facets[ key ];
				}
			} );

			// Append no analytics to the query so that the search doesn't get logged.
			newOptions.analytics = { tags: [ 'Facet-Only' ] };

			// Also set page size to 0 so that we don't get any results.
			newOptions.page.size = 0;

			// Append all required filters as well.
			requiredFilters.forEach( ( { key, value } ) => newOptions.filters.all.push( { [ key ]: value } ) );

			attributesDisjunctiveSearch = newOptions.facets && Object.keys( newOptions.facets ).length > 0 ? this.getClient().search( query, newOptions ) : new Promise( resolve => resolve() );
		}

		// Now we need to combine all the results into one.
		// We'll use the first result as the base, and then add the rest of the results to it.
		return Promise.all(
			[
				this.getClient().search( query, options ),
				attributesDisjunctiveSearch,
				...disjunctiveSearchesPromises,
			] ).then(
			( [ baseQueryResults, ...disjunctiveQueries ] ) => {
				disjunctiveQueries.filter( res => res?.rawInfo?.facets ).forEach( ( { rawInfo } ) => {
					Object.entries( rawInfo.facets ).forEach( ( [ facetName, facetValue ] ) => {
						baseQueryResults.rawInfo.facets[ facetName ] = facetValue;
					} );
				} );

				return baseQueryResults;
			}
		);
	}

	getClient() {
		if ( this.client ) {
			return this.client;
		}

		this.client = ElasticAppSearch.createClient( {
			searchKey: QalaElasticCore.searchAppConfig.searchKey,
			endpointBase: QalaElasticCore.searchAppConfig.endpointBase,
			engineName: QalaElasticCore.searchAppConfig.engineName,
			// This should be true but without this, all cached responses (i.e. results for queries user already submitted) return all result fields as undefined
			// It still seems to return the correct documents, just no data on them. Wasn't able to figure out why.
			cacheResponses: false,
		} );

		return this.client;
	}
}

